/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../nls/StringResources',
	'react',
	'prop-types',
	'classnames',
	'@ba/ba-ui-carbon',
	'@ba/ba-ui-carbon-toolkit',
	'./DataSourcePanelPage',
	'../DataSourcePanelIconHelper',
	'../../../glassControllers/MenuActionHelper',
	'../../../../util/DashboardFormatter'
], function (
	_,
	StringResources,
	React,
	PropTypes,
	classnames,
	Carbon,
	Toolkit,
	DataSourcePanelPage,
	DataSourcePanelIconHelper,
	MenuActionHelper,
	Formatter
) {

	const {
		Button,
		FlexItem,
		FlexLayout,
		Label,
		List,
		ListItem,
		SVGIcon
	} = Toolkit;

	const { InlineLoading } = Carbon;

	const GLASS_COLLECTION__BUTTONS = 'com.ibm.bi.dashboard.dataSourcePanel.list.buttons';
	const GLASS_COLLECTION__DATA_SOURCE__MENU = 'com.ibm.bi.dashboard.dataSources.dataSourceMenu';
	const DEFAULT_TIMEZONE = 'America/New_York';
	class DataSourcePanelList extends DataSourcePanelPage {
		static propTypes = {
			dataSources: PropTypes.array.isRequired,
			moserDatasources: PropTypes.object,
			onDataSourceSelected: PropTypes.func.isRequired
		}


		constructor(props) {
			super(props);

			this.state.dataSources = [];
			this.state.loadingPercentages = {};

			this._iconHelper = new DataSourcePanelIconHelper({
				iconsFeature: this.props.dashboardApi.getFeature('Icons')
			});

			this._dataSourcesSvc = this.props.dashboardApi.getFeature('dataSources.deprecated');

			this._buildState();
		}

		componentDidUpdate(prevProps) {
			if (this.props.dataSources !== prevProps.dataSources) {
				this._buildState();
			}
		}

		_registerProgressHandler(dataSourceIndex) {
			const dataSource = this.props.dataSources[dataSourceIndex];
			const dataSourceId = dataSource && dataSource.getId();
			const dataSourceState = dataSource && dataSource.getState();
			if(dataSourceState === 'loading' && !this._isMarkedLoading(dataSourceId)) {
				this.props.moserDatasources && this.props.moserDatasources.registerLoadMetadataProgressHandler(
					dataSourceId,
					this._updateLoadPercentage.bind(this, dataSourceId)
				);
				this.state.loadingPercentages[dataSourceId] = -1;
				dataSource.getModule().finally(() => {
					if(dataSource.getState() !== 'loading') {
						this._updateDataSourceState(dataSource);
						this._updateLoadPercentage(dataSourceId, {});
					}
				});
			}
		}

		_updateLoadPercentage(id, progressInfo) {
			const loadingPercentages = { ...this.state.loadingPercentages };
			loadingPercentages[id] = progressInfo.percentage;
			this.setState({ loadingPercentages });
		}

		_updateDataSourceState(dataSource) {
			this._initState.then(() => {
				const sources = this.state.dataSources;
				const sourceIndex = sources.findIndex(source => source.id === dataSource.getId());
				const source = sourceIndex !== -1 && sources[sourceIndex];
				if(source) {
					const dataSources = sources.slice();
					dataSources[sourceIndex].state = dataSource.getState();
					this.setState({ dataSources });
				}
			});
		}

		_isMarkedLoading(id) {
			return this.state.loadingPercentages[id] || this.state.loadingPercentages[id] === 0;
		}

		_getLoadingPercentage(id) {
			const percentage = this.state.loadingPercentages[id];
			if(percentage && percentage !== -1) {
				return `${percentage}%`;
			}
		}

		_buildState() {
			this._initState = Promise.all(
				this.props.dataSources.map(
					dataSource => Promise.all([
						dataSource.exists(),
						dataSource.getId(),
						dataSource.getAssetId(),
						dataSource.getLocalizedName(),
						dataSource.getModificationTime(),
						dataSource.getState(),
						dataSource.getType(),
						dataSource
					]).then(
						([
							exists,
							id,
							assetId,
							name,
							lastModified,
							state,
							type,
							api
						]) => ({
							exists,
							id,
							assetId,
							name,
							lastModified,
							state,
							type,
							api
						})
					)
				)
			).then(
				dataSources => this.setState({ dataSources })
			);
		}

		getTitleParams() {
			return {
				id: 'sourceList',
				title: StringResources.get('sourcePaneLabel')
			};
		}

		getButtonCollectionId() {
			return GLASS_COLLECTION__BUTTONS;
		}

		renderPanel() {
			return (
				<List
					className='dataSourcePanelDataSourceList'
					length={this.state.dataSources.length}
					itemRenderer={
						i => this.renderSourceItem(this.state.dataSources[i], i)
					}
					onSelectionChange={this.onSelectionChange.bind(this)}
				>
				</List>
			);
		}

		renderSourceItem(dataSource, index) {
			const dataSourceId = dataSource.id;
			const isInFocus = this.state.focusDataSourceId === dataSourceId;
			const dateTimeString = this._getDateTimeString(dataSource);
			this._registerProgressHandler(index);

			return (
				<ListItem
					className={
						classnames({
							'dataSourcePanelDataSourceListItem': true,
							'dataSourcePanelDataSourceListItem--enabled': this._canSelectDataSource(dataSource)
						})
					}
					selectable={this._canSelectDataSource(dataSource)}
					focus={isInFocus}
					listItemFocus={isInFocus}
				>
					<div
						className='dataSourcePanelDataSourceListItemFocusable'
						tabIndex='0'
						aria-label={dataSource.name}
						title={dataSource.name}
						onFocus={() => this.setState({ focusDataSourceId: dataSourceId })}
						onBlur={() => this.setState({ focusDataSourceId: null })}
						onContextMenu={e => this._onContextMenu(index, e)}
						onMouseOver={() => this.setState({ hoverDataSourceId: dataSourceId })}
						onMouseOut={() => this.setState({ hoverDataSourceId: null })}
						onKeyPress={e => this._onKeyPress(index, e)}
					>
						{index > 0 ? <div className='dataSourcePanelDivider' /> : null}
						<FlexLayout
							direction='row'
							className='dataSourcePanelDataSourceListItemStructure'
						>
							<FlexItem
								grow={false}
								shrink={false}
								className='dataSourcePanelListIcon'
							>
								{this.renderDataSourceIcon(dataSource)}
							</FlexItem>
							<FlexItem
								grow={true}
								shrink={true}
							>
								<div
									className='dataSourcePanelDataSourceInfo'
								>
									<FlexLayout
										direction='column'
									>
										<Label
											className='dataSourcePanelDataSourceListItemName'
											label={dataSource.name}
											ellipsis={true}
										/>
										{dateTimeString && (
											<Label
												className='dataSourcePanelDataSourceListItemDate'
												label={dateTimeString}
												type='caption'
												ellipsis={true}
											/>
										)}
									</FlexLayout>
								</div>
							</FlexItem>
							{this._isMarkedLoading(dataSourceId) && <FlexItem
								grow={false}
								shrink={false}
							>
								<InlineLoading
									iconDescription={StringResources.get('preparingData')}
									description={this._getLoadingPercentage(dataSourceId)}
									className='datasourcePanelListLoading'
								/>
							</FlexItem>}
							<FlexItem
								grow={false}
								shrink={false}
								className='dataSourcePanelListActions'
							>
								<Button
									iconSize='small'
									variant='icon'
									icon={this._iconHelper.getOverflowIcon()}
									onClick={event => this._onActionIconClick(dataSource, event)}	//TODO: Not working for keyboard
									aria-label={StringResources.get('moreTitle')}
									title={StringResources.get('moreTitle')}
									tooltipAlignment='start'
									tooltipPosition='left'
								/>
							</FlexItem>
						</FlexLayout>
					</div>
				</ListItem>
			);
		}

		renderDataSourceIcon(dataSource) {
			let iconId, intent, className;
			if(dataSource.state === 'error') {
				if (dataSource.type === 'dataSet2' && dataSource.exists === true) {
					iconId = this._iconHelper.getDataSourceWarningIcon();
					intent = 'warning';
				} else {
					iconId = this._iconHelper.getDataSourceErrorIcon();
					intent = 'danger';
				}
			} else {
				iconId = this._iconHelper.getDataSourceIcon(dataSource.type);
				className = 'is-dataSource';
			}
			return (
				<SVGIcon
					className={className}
					iconId={iconId}
					intent={intent}
					size='small'
				/>
			);
		}

		_getDateTimeString(dataSource) {
			const { lastModified: date } = dataSource;
			if (date) {
				const userProfile = this.props.dashboardApi.getGlassCoreSvc('.UserProfile');
				const timezone = userProfile && userProfile.preferences.timeZoneID || DEFAULT_TIMEZONE;

				const tokens = [
					Formatter.format(date, {
						type: 'date',
						formatLength: 'short',
						timezone: timezone
					}),
					Formatter.format(date, {
						type: 'time',
						formatLength: 'short',
						timezone: timezone
					})
				].filter(token => !!token);

				return tokens.join(' ');
			}
		}

		_canSelectDataSource(dataSource) {
			if(!dataSource) {
				return false;
			}

			if(dataSource.state === 'error') {
				return false;
			}

			if(!dataSource.exists) {
				return false;
			}

			if(this._isMarkedLoading(dataSource.id)) {
				return false;
			}

			return true;
		}

		_onContextMenu(index, event) {
			return this._openContextMenu(this.state.dataSources[index], event);
		}

		_onKeyPress(index, event) {
			//Guard against enter on the action menu
			if (event.target.getAttribute('class').indexOf('dataSourcePanelDataSourceListItemFocusable') >= 0) {
				if (event.key === 'Enter' || event.key === ' ') {
					return this.onSelectionChange({ index });
				}
			}
		}

		_onActionIconClick(dataSource, event) {
			event.stopPropagation();	//Don't open data source
			return this._openContextMenu(dataSource, event);
		}

		_openContextMenu(dataSource, event) {
			event.persist();	//Otherwise React SyntheticEvent gets garbage collected before it can be used

			if (!event.pageX) {
				delete event.pageX;
				delete event.pageY;
			}

			return MenuActionHelper.getActionPayload({
				'dashboardApi': this.props.dashboardApi,
				'id': dataSource.assetId,
				'type': dataSource.type,
				'event': event,
				'menuId': GLASS_COLLECTION__DATA_SOURCE__MENU,
				'position': {
					pageX: event.pageX,
					pageY: event.pageY
				}
			}).then(function (payload) {
				payload.activeObject.dataSource = dataSource.api;
				payload.activeObject.dataSourceName = dataSource.name;
				payload.activeObject.dataSourceCollection = this._dataSourcesSvc.getSourcesCollection();
				this.props.dashboardApi.showContextMenu(payload);
			}.bind(this));
		}

		onSelectionChange({ index }) {
			const dataSource = this.state.dataSources[index];
			const dataSourceApi = this.props.dataSources[index];
			if (this._canSelectDataSource(dataSource)) {
				this.props.onDataSourceSelected(dataSourceApi);
			}
		}
	}

	return DataSourcePanelList;
});
